import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  changeRejectReasonStatus,
  createRejectReason,
  deleteRejectReason,
  getRejectReasons,
  reorderRejectReasons,
  updateRejectReason,
} from '../api/rejectReasonApi'
import type { PagedResponse, RejectReason, RejectReasonStatus, RejectReasonUpsert } from '../types'

const KEY = 'admin-reject-reasons'

/**
 * Ret gerekçesi yönetimi sunucu-durumu: liste + oluştur/güncelle/durum/sil mutation'ları. Her başarıda liste
 * ve bilet lookup'ları (ret gerekçe seçenekleri) invalidate edilir — değişiklik ret popup'ına anında yansır
 * (FR-011). Güncel kayıt cache'e yazılır (bayat modDate → 409 önlenir). (011 useTicketTypes ile birebir.)
 */
export function useRejectReasons() {
  const { i18n } = useTranslation()
  const qc = useQueryClient()

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: [KEY] })
    qc.invalidateQueries({ queryKey: ['ticket-lookups'] })
  }

  const applyUpdated = (updated: RejectReason) =>
    qc.setQueryData<PagedResponse<RejectReason>>([KEY, i18n.language], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
        : prev,
    )

  const list = useQuery({
    queryKey: [KEY, i18n.language],
    queryFn: getRejectReasons,
  })

  // meta.handledCodes: domaine özel iş kodu ekranda ele alınır → merkezi hata toast'ı (019) susar.
  const create = useMutation({
    mutationFn: (body: RejectReasonUpsert) => createRejectReason(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RejectReasonUpsert }) =>
      updateRejectReason(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
    meta: { handledCodes: ['DUPLICATE_NAME'] },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RejectReasonStatus }) =>
      changeRejectReasonStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      invalidate()
    },
  })

  const remove = useMutation({
    mutationFn: (id: number) => deleteRejectReason(id),
    onSuccess: invalidate,
  })

  // Yeniden sıralama: liste anında (optimistic) yeni sıraya geçer; backend 1..N yazar. Hata → geri al.
  const reorder = useMutation({
    mutationFn: (orderedIds: number[]) => reorderRejectReasons(orderedIds),
    onMutate: async (orderedIds) => {
      await qc.cancelQueries({ queryKey: [KEY, i18n.language] })
      const prev = qc.getQueryData<PagedResponse<RejectReason>>([KEY, i18n.language])
      if (prev) {
        const byId = new Map(prev.content.map((r) => [r.id, r]))
        const content = orderedIds
          .map((id) => byId.get(id))
          .filter((r): r is RejectReason => r != null)
        qc.setQueryData<PagedResponse<RejectReason>>([KEY, i18n.language], { ...prev, content })
      }
      return { prev }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([KEY, i18n.language], ctx.prev)
    },
    onSettled: invalidate,
  })

  return { list, create, update, changeStatus, remove, reorder }
}
